/**
 * The booking URLs a business gives during onboarding are kept in localStorage rather
 * than on the server: they are only used to offer an import in the assistant right
 * after signup, on the same device and origin the business was created from.
 */
const STORAGE_KEY = 'breely_onboarding_booking_urls';

// Matches the window AssistantIdeas already uses to decide a business is new.
const MAX_AGE_MS = 48 * 60 * 60 * 1000;

export function saveOnboardingBookingUrls(urls, switchingFrom = '') {
    try {
        if (!urls?.length) {
            localStorage.removeItem(STORAGE_KEY);
            return;
        }
        localStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({ urls, switchingFrom, savedAt: Date.now() }),
        );
    } catch {
        // Private browsing and full quotas both throw; the import offer is optional.
    }
}

export function getOnboardingBookingUrls() {
    const empty = { urls: [], switchingFrom: '' };

    try {
        const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
        if (!stored || !Array.isArray(stored.urls) || !stored.urls.length) {
            return empty;
        }
        // The key is per-origin rather than per-business, so age it out instead of
        // offering an import for a business created weeks later.
        if (!stored.savedAt || Date.now() - stored.savedAt > MAX_AGE_MS) {
            return empty;
        }

        return {
            urls: stored.urls.filter(url => typeof url === 'string' && url.trim()),
            switchingFrom: stored.switchingFrom || '',
        };
    } catch {
        return empty;
    }
}

export function clearOnboardingBookingUrls() {
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch {
        // Nothing to do; the TTL will age the value out anyway.
    }
}

/**
 * An import touches most of a brand new account at once, so the prompt asks for a plan
 * the business can look over before anything is created.
 */
const CONFIRM_FIRST =
    'Plan out what changes you would make and show me a summary first.'
    + ' Do not create or update anything until I confirm I want to proceed.';

/**
 * The one prompt we offer for an import, listing every booking URL the business gave.
 */
export function importBookingPagesPrompt(urls) {
    if (!urls?.length) {
        return '';
    }

    const pages =
        urls.length === 1
            ? `Import settings from my booking page: ${urls[0]}`
            : `Import settings from my booking pages: ${urls.join(', ')}`;

    return `${pages}\n\n${CONFIRM_FIRST}`;
}
